import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, pct, thaiDateTime } from '../lib/format'

/**
 * Employee collusion / fraud-ring analysis (part 132).
 *
 * The server owns the graph algorithm: it builds edges from shared bank accounts, shared
 * registered addresses, vendor payout overlaps and approval pairs, groups them with union-find
 * and scores every component that clears the evidence threshold. This page never derives a ring
 * itself — it lists the stored rings and shows the stored edges, so the numbers in a screenshot
 * are the numbers the build wrote.
 */

type Band = 'low' | 'elevated' | 'high' | 'critical'

interface Member {
  id: string
  code: string
  name: string
  status: string
  bank_account: string
}

interface Signal {
  kind: string
  label: string
  key: string
  weight: number
  observations: number
  detail: string
}

interface Edge {
  id: string
  employee_a_id: string
  employee_b_id: string
  employee_a: string
  employee_b: string
  employee_a_code: string
  employee_b_code: string
  weight: number
  signals: Signal[]
}

interface Evidence {
  kind: string
  label: string
  weight: number
  edges: number
  observations: number
}

interface RingView {
  id: string
  ring_key: string
  score: number
  band: Band
  band_label: string
  member_count: number
  edge_count: number
  density: number
  average_weight: number
  top_signal: string
  signal_kinds: string[]
  explanation: string
  members: Member[]
  computed_at: string
}

interface RingDetail extends RingView {
  edges: Edge[]
  evidence: Evidence[]
}

interface BuildResponse {
  computed_at: string
  employees: number
  edges: number
  rings: number
  members: number
  high_risk: number
  top_score: number
  min_ring_size: number
  min_average_weight: number
}

const bandTone: Record<Band, string> = {
  low: 'sev-low',
  elevated: 'sev-medium',
  high: 'sev-high',
  critical: 'sev-critical',
}

const statusLabels: Record<string, string> = {
  active: 'ทำงานอยู่',
  terminated: 'พ้นสภาพ',
  unknown: 'ไม่ทราบสถานะ',
}

function statusLabel(value: string): string {
  return statusLabels[value] ?? value
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function memberSummary(members: Member[]): string {
  const names = members.slice(0, 3).map((m) => m.name)
  const extra = members.length > 3 ? ` และอีก ${members.length - 3} คน` : ''
  return `${names.join(', ')}${extra}`
}

export function Collusion() {
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const rings = useQuery({
    queryKey: ['collusion-rings'],
    queryFn: () => list<RingView>('/v1/collusion/rings?limit=200'),
    staleTime: 30_000,
  })

  const rows = rings.data ?? []
  const activeId = selectedId ?? rows[0]?.id ?? null

  const detail = useQuery({
    queryKey: ['collusion-ring', activeId],
    queryFn: () => api<RingDetail>(`/v1/collusion/rings/${activeId}`),
    enabled: Boolean(activeId),
    staleTime: 60_000,
  })

  const build = useMutation({
    mutationFn: () => apiWrite<BuildResponse>('/v1/collusion/build', 'POST'),
    onSuccess: (result) => {
      setNotice(
        `สร้างกราฟจากพนักงาน ${number(result.employees)} คน · พบเส้นเชื่อม ${number(result.edges)} เส้น ` +
        `และวงจร ${number(result.rings)} วงจร (คะแนนสูงสุด ${result.top_score}/100)`,
      )
      setSelectedId(null)
      void qc.invalidateQueries({ queryKey: ['collusion-rings'] })
      void qc.invalidateQueries({ queryKey: ['collusion-ring'] })
    },
  })

  const totalMembers = rows.reduce((sum, row) => sum + row.member_count, 0)
  const highRisk = rows.filter((row) => row.score >= 60).length
  const maxScore = rows.reduce((max, row) => Math.max(max, row.score), 0)
  const active = detail.data

  return (
    <>
      <PageHead
        title="วงจรสมคบคิดพนักงาน"
        sub="ตรวจหาการรวมกลุ่มผิดปกติจากบัญชีเงินฝาก ที่อยู่ ผู้ขาย และการอนุมัติไขว้ · ทุกวงจรอธิบายด้วยเส้นเชื่อมและน้ำหนักหลักฐาน"
        actions={
          <button
            className="btn btn-primary btn-sm"
            disabled={build.isPending}
            onClick={() => { setNotice(null); build.mutate() }}
          >
            {build.isPending ? 'กำลังวิเคราะห์…' : 'วิเคราะห์ใหม่'}
          </button>
        }
      />

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 4, marginBottom: 16, padding: '10px 14px', fontSize: 12.5,
            border: '1px solid var(--color-emerald-soft)', borderRadius: 6,
            color: 'var(--color-emerald-soft)',
          }}
        >
          {notice}
        </div>
      ) : null}

      {build.isError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {errorText(build.error, 'วิเคราะห์กราฟไม่สำเร็จ')}
        </div>
      ) : null}

      <div className="grid-4 stagger">
        <Stat label="วงจรที่ตรวจพบ" value={number(rows.length)} unit="วงจร" foot="เฉพาะกลุ่มที่ผ่านเกณฑ์หลักฐานขั้นต่ำ" />
        <Stat label="พนักงานในวงจร" value={number(totalMembers)} unit="คน" foot="นับจากสมาชิกของทุกวงจร" />
        <Stat label="คะแนนสูงสุด" value={maxScore} unit="/ 100" foot="ถ่วงน้ำหนักจากความแรงและความแน่นของเส้นเชื่อม" />
        <Stat
          label="วงจรเสี่ยงสูง"
          value={number(highRisk)}
          unit="วงจร"
          foot={build.data ? `พนักงานทั้งหมด ${number(build.data.employees)} คน` : 'กด “วิเคราะห์ใหม่” เพื่อสร้างกราฟล่าสุด'}
        />
      </div>

      {rings.isLoading ? (
        <div className="stack" style={{ marginTop: 16 }}><Skel h={200} /><Skel h={160} /></div>
      ) : rings.isError ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="form-error">{errorText(rings.error, 'โหลดรายการวงจรไม่สำเร็จ')}</div>
        </div>
      ) : rows.length === 0 ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <Empty
            title="ยังไม่พบวงจรสมคบคิด"
            body="กด “วิเคราะห์ใหม่” เพื่อสร้างกราฟจากบัญชีเงินฝาก ที่อยู่ตามทะเบียน ผู้ขาย และประวัติการอนุมัติ แล้วจัดกลุ่มพนักงานที่เชื่อมโยงกันตั้งแต่สามคนขึ้นไป"
          />
        </div>
      ) : (
        <div className="grid-main" style={{ marginTop: 16 }}>
          <div className="card card-pad">
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">วงจรที่ตรวจพบ</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                เรียงตามคะแนนความเสี่ยง
              </span>
            </div>
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>วงจร</th>
                    <th style={{ textAlign: 'right' }}>ความเสี่ยง</th>
                    <th style={{ textAlign: 'right' }}>สมาชิก</th>
                    <th style={{ textAlign: 'right' }}>เส้นเชื่อม</th>
                    <th>หลักฐานหลัก</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedId(row.id)}
                      style={{
                        cursor: 'pointer',
                        background: row.id === activeId ? 'rgba(200, 169, 106, 0.06)' : undefined,
                      }}
                    >
                      <td>
                        <div>{memberSummary(row.members)}</div>
                        <div className="dim" style={{ fontSize: 11 }}>{row.ring_key}</div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className={`sev ${bandTone[row.band]}`}>
                          <i className="sev-dot" />
                          {row.score}
                        </span>
                        <div className="dim" style={{ fontSize: 10.5 }}>{row.band_label}</div>
                      </td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{row.member_count}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{row.edge_count}</td>
                      <td>{row.top_signal || 'ไม่ระบุ'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="dim" style={{ fontSize: 11, marginTop: 10 }}>
              เกณฑ์ขั้นต่ำ: สมาชิกอย่างน้อย 3 คน · น้ำหนักเส้นเชื่อมเฉลี่ยตั้งแต่ 30 ขึ้นไป ·
              หลักฐานแต่ละประเภทคือบัญชีเงินฝากร่วม 50, ที่อยู่ร่วม 25, ผู้ขายร่วม 15 และอนุมัติไขว้ 10 คะแนน
            </div>
          </div>

          <div className="card card-pad">
            {detail.isLoading ? (
              <Skel h={240} />
            ) : detail.isError ? (
              <div className="form-error">{errorText(detail.error, 'โหลดคำอธิบายวงจรไม่สำเร็จ')}</div>
            ) : !active ? (
              <Empty title="ยังไม่ได้เลือกวงจร" body="เลือกวงจรจากตารางเพื่อดูสมาชิกและหลักฐานรายเส้น" />
            ) : (
              <>
                <div className="card-label">คำอธิบายวงจร</div>
                <div className="row" style={{ alignItems: 'baseline', marginTop: 10 }}>
                  <span style={{ fontSize: 30, fontWeight: 300 }} className="tnum">{active.score}</span>
                  <span className="dim" style={{ fontSize: 12 }}>/ 100</span>
                  <span className={`sev ${bandTone[active.band]}`} style={{ marginLeft: 8 }}>
                    <i className="sev-dot" />
                    {active.band_label}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, marginTop: 8 }}>{active.explanation}</div>
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  {active.member_count} คน · {active.edge_count} เส้น · ความแน่น {pct(active.density)} ·
                  น้ำหนักเฉลี่ย {active.average_weight.toFixed(1)}
                </div>

                <div className="row" style={{ gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                  {active.signal_kinds.map((kind) => (
                    <span key={kind} className="chip gold">{active.evidence.find((e) => e.kind === kind)?.label ?? kind}</span>
                  ))}
                </div>

                <div style={{ marginTop: 18 }}>
                  <div className="card-label">สมาชิกในวงจร</div>
                  <div className="stack" style={{ gap: 8, marginTop: 10 }}>
                    {active.members.map((member) => (
                      <div key={member.id} className="row" style={{ alignItems: 'baseline' }}>
                        <div>
                          <div style={{ fontSize: 12.5 }}>{member.name}</div>
                          <div className="dim" style={{ fontSize: 10.5 }}>
                            {member.code} · {statusLabel(member.status)}
                            {member.bank_account ? ` · บัญชี ${member.bank_account}` : ''}
                          </div>
                        </div>
                        <span className="dim tnum" style={{ marginLeft: 'auto', fontSize: 10.5 }}>
                          {member.id.slice(0, 8)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: 18 }}>
                  <div className="card-label">เส้นเชื่อมและน้ำหนักหลักฐาน</div>
                  <div className="stack" style={{ gap: 12, marginTop: 10 }}>
                    {active.edges.length === 0 ? (
                      <div className="dim" style={{ fontSize: 12 }}>ไม่พบเส้นเชื่อมภายในวงจรนี้</div>
                    ) : (
                      active.edges.map((edge) => (
                        <div key={edge.id} style={{ borderTop: '1px solid var(--color-hairline)', paddingTop: 10 }}>
                          <div className="row" style={{ alignItems: 'baseline' }}>
                            <span style={{ fontSize: 12.5 }}>
                              {edge.employee_a} ({edge.employee_a_code})
                              <span className="dim"> ↔ </span>
                              {edge.employee_b} ({edge.employee_b_code})
                            </span>
                            <span className="tnum" style={{ marginLeft: 'auto', fontSize: 12 }}>
                              น้ำหนัก {edge.weight}
                            </span>
                          </div>
                          <div className="bar" style={{ marginTop: 6, width: '100%' }}>
                            <i
                              style={{
                                width: `${Math.min(100, edge.weight)}%`,
                                background: edge.weight >= 80
                                  ? 'var(--color-crimson)'
                                  : edge.weight >= 50
                                    ? 'var(--color-amber)'
                                    : 'var(--color-gold)',
                              }}
                            />
                          </div>
                          <div className="stack" style={{ gap: 4, marginTop: 8 }}>
                            {edge.signals.map((signal) => (
                              <div key={`${signal.kind}:${signal.key}`} className="dim" style={{ fontSize: 11 }}>
                                <span style={{ color: 'var(--color-ivory)' }}>{signal.label}</span>
                                {' · '}
                                {signal.detail}
                                {signal.observations > 1 ? ` · พบ ${number(signal.observations)} ครั้ง` : ''}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="dim" style={{ fontSize: 10.5, marginTop: 14 }}>
                  ประเมินเมื่อ {thaiDateTime(active.computed_at)} · รหัสวงจร {active.ring_key}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}
